import React, { useState } from 'react';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';

const { RangePicker } = DatePicker;

interface Props {
	startTime: string;
	endTime: string;
	onChange: (startUtc: string, endUtc: string) => void;
	onError: (msg: string | null) => void;
}

const DateRangePickerUTC: React.FC<Props> = ({
	startTime,
	endTime,
	onChange,
	onError,
}) => {
	const [open, setOpen] = useState(false);
	const [internalDates, setInternalDates] = useState<
		[Moment | null, Moment | null] | null
	>(null);

	const getValue = () => {
		if (
			internalDates &&
			internalDates[0]?.isValid() &&
			internalDates[1]?.isValid()
		) {
			return internalDates;
		}

		const startValid =
			startTime &&
			!Number.isNaN(Number(startTime)) &&
			moment.utc(Number(startTime)).isValid();
		const endValid =
			endTime &&
			!Number.isNaN(Number(endTime)) &&
			moment.utc(Number(endTime)).isValid();

		const startMoment = startValid ? moment.utc(Number(startTime)) : null;
		const endMoment = endValid ? moment.utc(Number(endTime)) : null;

		return [startMoment, endMoment] as [Moment | null, Moment | null];
	};

	const disabledDate = (current: Moment) => {
		if (!current) return false;
		return current.isAfter(moment.utc().endOf('day'));
	};

	return (
		<RangePicker
			style={{ width: '100%' }}
			value={getValue()}
			disabledDate={disabledDate}
			format="YYYY-MM-DD"
			allowClear
			open={open}
			onOpenChange={(status) => {
				setOpen(status);
				if (!status) {
					setInternalDates(null); // Reset internal state when closing
				}
			}}
			onChange={(dates) => {
				if (
					dates &&
					dates.length === 2 &&
					dates[0]?.isValid() &&
					dates[1]?.isValid()
				) {
					setInternalDates([dates[0], dates[1]]);
					const startUtc = moment
						.utc(dates[0].format('YYYY-MM-DD'))
						.startOf('day');
					const endUtc = moment.utc(dates[1].format('YYYY-MM-DD')).endOf('day');

					const diffDays = endUtc.diff(startUtc, 'day');
					if (diffDays > 90) {
						onError('Date range cannot exceed 90 days.');
						onChange('', '');
						setInternalDates(null);
						setOpen(false);
						return;
					}

					onChange(startUtc.valueOf().toString(), endUtc.valueOf().toString());
					onError(null);
				} else {
					setInternalDates(null);
					onError('Invalid date range selected.');
					onChange('', '');
				}
			}}
			monthCellRender={(date) => moment.utc(date).format('MMMM YYYY')}
		/>
	);
};

export default DateRangePickerUTC;
